/**
 * Route table (ARCHITECTURE.md §3).
 *
 * Every page is code-split with `React.lazy`. The page modules and `AppLayout` are owned by the
 * component/design agent — this file only declares where they mount and who may reach them.
 *
 * `main.tsx` supplies the `<BrowserRouter>`, so this exports a `<Routes>` tree rather than a
 * `RouterProvider` (the two cannot be nested).
 */

import { Fragment, Suspense, lazy, type ReactElement } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { ProtectedRouteFallback } from '@/components/common/ProtectedRouteFallback';
import { PlanGate } from '@/features/billing/components/PlanGate';
import { PAGE_LOCALES, localePath } from '@/seo/site';
import { useAuthStore } from '@/stores/authStore';
import { authDestination } from '@/lib/authDestination';

/* -------------------------------------------------------------------------- */
/* Lazy pages — created by the component agent, not by this layer              */
/* -------------------------------------------------------------------------- */

const AppLayout = lazy(() => import('@/components/layout/AppLayout'));

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ResultsPage = lazy(() => import('@/pages/ResultsPage'));
//: One module for the three legal texts: it is prerendered once per path.
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const BotsPage = lazy(() => import('@/pages/BotsPage'));
const BotCreatePage = lazy(() => import('@/pages/BotCreatePage'));
const BotDetailPage = lazy(() => import('@/pages/BotDetailPage'));
const BotEditPage = lazy(() => import('@/pages/BotEditPage'));
const DealsPage = lazy(() => import('@/pages/DealsPage'));
const DealDetailPage = lazy(() => import('@/pages/DealDetailPage'));
const PortfolioPage = lazy(() => import('@/pages/PortfolioPage'));
const InsightsPage = lazy(() => import('@/pages/InsightsPage'));
const AutopilotPage = lazy(() => import('@/pages/AutopilotPage'));
const ShowcasePage = lazy(() => import('@/pages/ShowcasePage'));
const VolatilityPage = lazy(() => import('@/pages/VolatilityPage'));
const VolumePage = lazy(() => import('@/pages/VolumePage'));
const LabPage = lazy(() => import('@/pages/LabPage'));
const CopyTradePage = lazy(() => import('@/pages/CopyTradePage'));
const CopyTradeProfilePage = lazy(() => import('@/pages/CopyTradeProfilePage'));
const BacktestPage = lazy(() => import('@/pages/BacktestPage'));
const WebSourcesPage = lazy(() => import('@/pages/WebSourcesPage'));
const ScannerPage = lazy(() => import('@/pages/ScannerPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const InvoicePage = lazy(() => import('@/pages/InvoicePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ChangelogPage = lazy(() => import('@/pages/ChangelogPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminUserPage = lazy(() => import('@/pages/AdminUserPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/* -------------------------------------------------------------------------- */
/* Route paths — import these instead of typing string literals                */
/* -------------------------------------------------------------------------- */

export const ROUTES = {
  login: '/login',
  register: '/register',
  home: '/',
  //: docs/SHOWCASE.md §6.2 — the showcase without signing in: the results page to share.
  results: '/results',
  //: docs/LEGAL.md — the terms of use, the privacy and KVKK notice, the risk notice.
  terms: '/terms',
  privacy: '/privacy',
  risk: '/risk',
  dashboard: '/dashboard',
  bots: '/bots',
  botNew: '/bots/new',
  botDetail: (id: string) => `/bots/${id}`,
  botEdit: (id: string) => `/bots/${id}/edit`,
  deals: '/deals',
  dealDetail: (id: string) => `/deals/${id}`,
  //: The account itself — positions and resting orders on the exchange (§6.8).
  portfolio: '/positions',
  //: Analyses over the trading — the suggestion scorecard first (docs/SCORECARD.md); tabs at
  //: `/insights/:tab` come from features/insights/tabs.ts.
  insights: '/insights',
  //: docs/AUTOPILOT.md — real bots from the live tuning pass's picks, once the user switches it on.
  autopilot: '/autopilot',
  //: docs/SHOWCASE.md — working bots to copy: users' own, anonymous, and system-tested ones.
  showcase: '/showcase',
  //: docs/VOLATILITY.md — the most volatile coins on 1-minute candles, and a quick bot on one.
  volatility: '/volatility',
  //: docs/VOLUME.md — the coins whose volume jumped on 1-minute candles, and a bot that may open
  //: on them by itself (only the Admin package for now: "Future" in the menu).
  volume: '/volume',
  //: docs/SIGNAL_LAB.md — every signal source on paper under the same rules, compared (Admin
  //: package only: "Future" in the menu). Opens no bot.
  lab: '/lab',
  //: docs/COPY_TRADE.md — publishers' bots to follow on one's own account for a share of the
  //: profit, and one's own to publish (Admin package only: "Future" in the menu). The tab is in
  //: `?tab=` (features/copyTrade/routes.ts).
  copyTrade: '/copy-trade',
  //: A publisher's page by nickname — what a community shares to send people to them.
  copyTradePublisher: (nickname: string) => `/copy-trade/u/${encodeURIComponent(nickname)}`,
  //: docs/BACKTEST.md — DCA settings replayed on a pair's history, without opening a bot.
  backtest: '/backtest',
  //: docs/WEB_SOURCES.md — another page's live wallet, read on a timer and linked to bots.
  webSources: '/web-sources',
  //: docs/SCANNER.md — the scanner, its own page since it is a package feature (was a Settings tab).
  scanner: '/scanner',
  //: docs/BILLING.md — the packages, the user's own, checkout and the payment history.
  billing: '/billing',
  billingInvoice: (id: string) => `/billing/invoices/${id}`,
  settings: '/settings',
  // Exchange accounts is a tab of Settings (§6.9); the old top-level paths redirect here.
  exchanges: '/settings/exchange-accounts',
  //: What shipped, newest first (features/changelog/entries.ts). Administrators only.
  changelog: '/changelog',
  //: Administrators only — docs/ADMIN.md. Sub-paths live in features/admin/routes.ts.
  admin: '/admin',
} as const;

/* -------------------------------------------------------------------------- */
/* Suspense / guard fallback — the visual is owned by the design layer          */
/* -------------------------------------------------------------------------- */

function RouteFallback(): ReactElement {
  return <ProtectedRouteFallback state="checking" />;
}

/* -------------------------------------------------------------------------- */
/* Guards                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Blocks a route until the user is authenticated, preserving the attempted location in
 * `location.state.from` so `PublicOnlyRoute` can send them back after login.
 *
 * While `bootstrap()` is still running we render nothing rather than bouncing to `/login` —
 * otherwise a reload with a valid token would always flash the login screen.
 */
export function ProtectedRoute(): ReactElement | null {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isBootstrapping = useAuthStore((state) => state.isBootstrapping);
  const location = useLocation();

  if (isBootstrapping) return <RouteFallback />;
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }
  return <Outlet />;
}

/**
 * docs/ADMIN.md §8 — administrators only. Everyone else gets the not-found page rather than a
 * "forbidden" one: the area's existence is nobody else's business. The API refuses them anyway.
 */
export function AdminRoute(): ReactElement {
  const isSuperuser = useAuthStore((state) => state.user?.is_superuser === true);
  if (!isSuperuser) return <NotFoundPage />;
  return <Outlet />;
}

/** Keeps a signed-in user out of `/login` and `/register`, returning them where they came from. */
export function PublicOnlyRoute(): ReactElement | null {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isBootstrapping = useAuthStore((state) => state.isBootstrapping);
  const location = useLocation();

  if (isBootstrapping) return <RouteFallback />;
  if (isAuthenticated) {
    return <Navigate to={authDestination(location.state)} replace />;
  }
  return <Outlet />;
}

/* -------------------------------------------------------------------------- */
/* Route tree                                                                 */
/* -------------------------------------------------------------------------- */

export function AppRoutes(): ReactElement {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public, at each language's address: `/login`, and `/tr/login` (seo/site.ts). */}
        {PAGE_LOCALES.map((locale) => {
          const at = (path: string) => localePath(path, locale);
          return (
            <Fragment key={locale}>
              <Route path={at(ROUTES.home)} element={<LandingPage />} />
              {/* Open to everyone, signed in or not: its cards' buttons know which. */}
              <Route path={at(ROUTES.results)} element={<ResultsPage />} />
              {/* docs/LEGAL.md — open to everyone, like the results page. */}
              <Route path={at(ROUTES.terms)} element={<LegalPage doc="terms" />} />
              <Route path={at(ROUTES.privacy)} element={<LegalPage doc="privacy" />} />
              <Route path={at(ROUTES.risk)} element={<LegalPage doc="risk" />} />
              <Route element={<PublicOnlyRoute />}>
                <Route path={at(ROUTES.login)} element={<LoginPage />} />
                <Route path={at(ROUTES.register)} element={<RegisterPage />} />
              </Route>
            </Fragment>
          );
        })}

        {/* Authenticated shell */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path={ROUTES.dashboard} element={<DashboardPage />} />

            <Route path="bots">
              <Route index element={<BotsPage />} />
              <Route path="new" element={<BotCreatePage />} />
              <Route path=":id" element={<BotDetailPage />} />
              <Route path=":id/edit" element={<BotEditPage />} />
            </Route>

            <Route path="deals">
              <Route index element={<DealsPage />} />
              <Route path=":id" element={<DealDetailPage />} />
            </Route>

            <Route path="positions" element={<PortfolioPage />} />

            {/* docs/BILLING.md §5 — the package features: a locked page says so and links to the
                packages; the API refuses on its own too. */}
            <Route
              path="insights"
              element={
                <PlanGate feature="insights">
                  <InsightsPage />
                </PlanGate>
              }
            />
            <Route
              path="insights/:tab"
              element={
                <PlanGate feature="insights">
                  <InsightsPage />
                </PlanGate>
              }
            />

            <Route
              path="autopilot"
              element={
                <PlanGate feature="autopilot">
                  <AutopilotPage />
                </PlanGate>
              }
            />

            <Route path="showcase" element={<ShowcasePage />} />

            <Route
              path="volatility"
              element={
                <PlanGate feature="volatility">
                  <VolatilityPage />
                </PlanGate>
              }
            />
            <Route
              path="volume"
              element={
                <PlanGate feature="volume">
                  <VolumePage />
                </PlanGate>
              }
            />
            <Route
              path="lab"
              element={
                <PlanGate feature="signal_lab">
                  <LabPage />
                </PlanGate>
              }
            />
            <Route
              path="copy-trade"
              element={
                <PlanGate feature="copy_trade">
                  <CopyTradePage />
                </PlanGate>
              }
            />
            <Route
              path="copy-trade/u/:nickname"
              element={
                <PlanGate feature="copy_trade">
                  <CopyTradeProfilePage />
                </PlanGate>
              }
            />
            <Route
              path="backtest"
              element={
                <PlanGate feature="backtest">
                  <BacktestPage />
                </PlanGate>
              }
            />

            <Route
              path="web-sources"
              element={
                <PlanGate feature="web_sources">
                  <WebSourcesPage />
                </PlanGate>
              }
            />

            <Route
              path="scanner"
              element={
                <PlanGate feature="scanner">
                  <ScannerPage />
                </PlanGate>
              }
            />
            <Route path="settings/scanner" element={<Navigate to={ROUTES.scanner} replace />} />

            <Route path="billing" element={<BillingPage />} />
            <Route path="billing/invoices/:id" element={<InvoicePage />} />

            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/:tab" element={<SettingsPage />} />
            {/* Release notes are for administrators only; everyone else gets the 404 page. */}
            <Route path="changelog" element={<AdminRoute />}>
              <Route index element={<ChangelogPage />} />
            </Route>

            <Route path="admin" element={<AdminRoute />}>
              <Route index element={<AdminPage />} />
              <Route path="users/:id" element={<AdminUserPage />} />
              <Route path=":tab" element={<AdminPage />} />
            </Route>
            {/* Kept so older links and bookmarks don't land on the 404 page. */}
            <Route path="exchange-accounts" element={<Navigate to={ROUTES.exchanges} replace />} />
            <Route path="exchanges" element={<Navigate to={ROUTES.exchanges} replace />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        {/* Anything outside the shell (e.g. hit while logged out) */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
